'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import {
  Dialog as RACDialog,
  Heading as RACHeading,
  Modal as RACModal,
  ModalOverlay as RACModalOverlay,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { IconCheckCircle, IconInfoCircle, IconWarning, IconWarningCircleFill } from '../../icons/index.js';
import { FocusableContainer } from '../internal/focusable-container/focusable-container.js';
import { Stack } from '../spacing/stack/stack.js';
import type { CommonProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

const iconMap = {
  info: IconInfoCircle,
  success: IconCheckCircle,
  warning: IconWarning,
  critical: IconWarningCircleFill,
};

export interface AlertDialogRenderProps {
  /** Closes the alert dialog when called. */
  close: () => void;
}

export interface AlertDialogProps extends CommonProps {
  /** The title of the alert dialog. */
  title: string;
  /** The contents of the alert dialog. A function may be provided to access a function to close the dialog. */
  children: ReactNode | ((renderProps: AlertDialogRenderProps) => ReactNode);
  /** The tone of the alert dialog. */
  tone: 'info' | 'success' | 'warning' | 'critical';
  /**
   * Whether the dialog is open (controlled).
   * If using `DialogRoot`, this prop has no effect - provide `isOpen` to `DialogRoot` instead.
   */
  isOpen?: boolean;
  /**
   * Whether the dialog is open by default (uncontrolled).
   * If using `DialogRoot`, this prop has no effect - provide `defaultOpen` to `DialogRoot` instead.
   */
  defaultOpen?: boolean;
  /**
   * Handler that is called when the dialog's open state changes.
   * If using `DialogRoot`, this prop has no effect - provide `onOpenChange` to `DialogRoot` instead.
   */
  onOpenChange?: (isOpen: boolean) => void;
}

function AlertDialog(
  {
    children,
    title,
    tone,
    UNSAFE_className,
    UNSAFE_style,
    isOpen,
    defaultOpen,
    onOpenChange,
    ...props
  }: AlertDialogProps,
  ref: ForwardedRef<HTMLElement>,
) {
  const Icon = iconMap[tone];

  return (
    <RACModalOverlay
      className="cim-modal-overlay"
      isDismissable={false}
      isKeyboardDismissDisabled
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      data-cim-style-root
    >
      <RACModal className={clsx('cim-alert-dialog-modal', UNSAFE_className)} style={UNSAFE_style}>
        <RACDialog {...props} ref={ref} role="alertdialog" className="cim-alert-dialog" data-tone={tone}>
          {(renderProps) => (
            <>
              <div className="cim-alert-dialog-icon">
                <Icon size={32} />
              </div>

              <RACHeading
                slot="title"
                className={clsx('cim-alert-dialog-title', textStyle({ variant: 'title-6', tone: 'base' }))}
              >
                {title}
              </RACHeading>

              {typeof children === 'function' ? children(renderProps) : children}
            </>
          )}
        </RACDialog>
      </RACModal>
    </RACModalOverlay>
  );
}

/**
 * Renders an overlay that interrupts user activity and requires a user action.
 *
 * See [alert dialog usage guidelines](https://ui.cimpress.io/components/alert-dialog/).
 */
const _AlertDialog = forwardRef(AlertDialog, 'AlertDialog');

export { _AlertDialog as AlertDialog };

export interface AlertDialogBodyProps {
  /** The content to render within the alert dialog. */
  children: ReactNode;
}

/** Renders content within `AlertDialog`. */
export function AlertDialogBody(props: AlertDialogBodyProps) {
  return (
    <FocusableContainer
      {...props}
      className={clsx('cim-alert-dialog-content', textStyle({ variant: 'medium', tone: 'base' }))}
      scrollDirection="vertical"
    />
  );
}

AlertDialogBody.displayName = 'AlertDialogBody';

export interface AlertDialogActionsProps {
  /** Actions that should be available in the alert dialog. */
  children: ReactNode;
}

/** Renders actions within `AlertDialog`. */
export function AlertDialogActions({ children, ...props }: AlertDialogActionsProps) {
  return (
    <Stack {...props} gap={16} direction="horizontal" justify="end" marginTop={24}>
      {children}
    </Stack>
  );
}

AlertDialogActions.displayName = 'AlertDialogActions';
